import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ColorNuancierPicker from './ColorNuancierPicker';
import { normalizeHex } from '../utils/colorNuancier';

export interface ColorSelectOption {
  value: string;
  label: string;
  hex: string | null;
}

interface ColorSelectFieldProps {
  label: string;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  options: ColorSelectOption[];
  errorMessage?: string;
  dataField?: string;
  allowCustomHex?: boolean;
}

const isHexColor = (candidate: string) => /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(candidate.trim());

const isOtherColorOption = (candidate: string) => {
  const normalized = candidate.trim().toLowerCase();
  return normalized === 'autre' || normalized === 'other';
};

function chunkOptions<T>(items: T[], columnCount: number): T[][] {
  const rows: T[][] = [];
  for (let index = 0; index < items.length; index += columnCount) {
    rows.push(items.slice(index, index + columnCount));
  }
  return rows;
}

const ColorSelectField: React.FC<ColorSelectFieldProps> = ({
  label,
  required = false,
  value,
  onChange,
  options,
  errorMessage,
  dataField,
  allowCustomHex = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [showHsvPickerOnly, setShowHsvPickerOnly] = useState(false);
  const [hsvDraftColor, setHsvDraftColor] = useState('#808080');
  const fieldRef = useRef<HTMLDivElement>(null);

  const optionValues = useMemo(() => new Set(options.map((option) => option.value)), [options]);
  const optionHexes = useMemo(
    () => new Set(options.map((option) => (option.hex || '').trim().toLowerCase()).filter(Boolean)),
    [options],
  );

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value],
  );

  const isCustomHexValue =
    allowCustomHex
    && isHexColor(value || '')
    && !optionHexes.has((value || '').trim().toLowerCase())
    && !optionValues.has((value || '').trim());

  const displayLabel = selectedOption?.label
    ?? (isCustomHexValue ? 'Couleur personnalisée' : value || '');

  const displayHex = selectedOption?.hex ?? (isCustomHexValue ? value : null);

  const handleOpenPicker = (event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
    setShowHsvPickerOnly(false);
    const target = (event?.currentTarget as HTMLElement | undefined) ?? fieldRef.current;
    if (target) {
      setAnchorEl(target);
    }
    setPickerOpen(true);
  };

  const handleClosePicker = () => {
    setPickerOpen(false);
    setAnchorEl(null);
    setShowHsvPickerOnly(false);
  };

  const handleSelect = (nextValue: string) => {
    if (isOtherColorOption(nextValue)) {
      setHsvDraftColor(isCustomHexValue ? normalizeHex(value || '') : '#808080');
      setShowHsvPickerOnly(true);
      return;
    }
    onChange(nextValue);
    handleClosePicker();
  };

  const handleApplyHsvColor = () => {
    onChange(normalizeHex(hsvDraftColor));
    handleClosePicker();
  };

  const tableColumns = isMobile ? 3 : 4;
  const colorRows = chunkOptions(options, tableColumns);

  const renderColorCell = (option: ColorSelectOption) => {
    const isSelected = value === option.value || (isCustomHexValue && isOtherColorOption(option.value));
    return (
      <Box
        role="button"
        tabIndex={0}
        onClick={() => handleSelect(option.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleSelect(option.value);
          }
        }}
        sx={{
          cursor: 'pointer',
          textAlign: 'center',
          borderRadius: 1.5,
          p: 1,
          height: '100%',
          border: '2px solid',
          borderColor: isSelected ? 'primary.main' : 'divider',
          bgcolor: isSelected ? 'action.selected' : 'background.paper',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            mx: 'auto',
            mb: 0.75,
            bgcolor: option.hex || '#f5f5f5',
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isOtherColorOption(option.label) || isOtherColorOption(option.value) ? (
            <PaletteOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
          ) : null}
        </Box>
        <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.2 }}>
          {option.label}
        </Typography>
      </Box>
    );
  };

  const pickerContent = showHsvPickerOnly ? (
    <Box sx={{ p: 2, width: isMobile ? '100%' : 320 }}>
      <ColorNuancierPicker
        value={hsvDraftColor}
        onChange={setHsvDraftColor}
        onConfirm={handleApplyHsvColor}
      />
    </Box>
  ) : (
    <Box sx={{ p: 2, width: isMobile ? '100%' : tableColumns * 92 }}>
      <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
        <TableBody>
          {colorRows.map((row, rowIndex) => (
            <TableRow key={`color-row-${rowIndex}`}>
              {row.map((option) => (
                <TableCell
                  key={option.value}
                  align="center"
                  sx={{ border: 0, p: 0.5, verticalAlign: 'top', width: `${100 / tableColumns}%` }}
                >
                  {renderColorCell(option)}
                </TableCell>
              ))}
              {row.length < tableColumns
                ? Array.from({ length: tableColumns - row.length }).map((_, emptyIndex) => (
                    <TableCell
                      key={`empty-${rowIndex}-${emptyIndex}`}
                      sx={{ border: 0, p: 0.5, width: `${100 / tableColumns}%` }}
                    />
                  ))
                : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );

  return (
    <FormControl
      fullWidth
      required={required}
      error={Boolean(errorMessage)}
      data-field={dataField}
    >
      <InputLabel shrink={Boolean(value)} required={required}>
        {label}
      </InputLabel>
      <OutlinedInput
        ref={fieldRef}
        readOnly
        notched={Boolean(value)}
        label={label}
        value={displayLabel}
        placeholder={value ? undefined : 'Choisir une couleur'}
        onClick={handleOpenPicker}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleOpenPicker(event);
          }
        }}
        inputProps={{
          'aria-haspopup': 'dialog',
          readOnly: true,
        }}
        startAdornment={
          displayHex ? (
            <InputAdornment position="start">
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  bgcolor: displayHex,
                  border: displayHex.toUpperCase() === '#FFFFFF' ? '1px solid #ccc' : '1px solid',
                  borderColor: 'divider',
                  flexShrink: 0,
                }}
              />
            </InputAdornment>
          ) : undefined
        }
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              edge="end"
              aria-label={`Choisir ${label.toLowerCase()}`}
              onClick={(event) => {
                event.stopPropagation();
                handleOpenPicker(event);
              }}
              size="small"
            >
              <PaletteOutlinedIcon />
            </IconButton>
          </InputAdornment>
        }
        sx={{
          cursor: 'pointer',
          '& .MuiOutlinedInput-input': {
            cursor: 'pointer',
            fontWeight: value ? 600 : 400,
          },
        }}
      />
      {errorMessage ? <FormHelperText>{errorMessage}</FormHelperText> : null}

      {isMobile ? (
        <Dialog open={pickerOpen} onClose={handleClosePicker} fullWidth maxWidth="xs">
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
            {showHsvPickerOnly ? 'Couleur personnalisée' : label}
            <IconButton aria-label="Fermer" onClick={handleClosePicker} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ position: 'relative' }}>{pickerContent}</DialogContent>
        </Dialog>
      ) : (
        <Popover
          open={pickerOpen}
          onClose={handleClosePicker}
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {pickerContent}
        </Popover>
      )}
    </FormControl>
  );
};

export default ColorSelectField;
